import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ColDef, GridApi, SelectionChangedEvent } from "ag-grid-community";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { AgGridReact } from "ag-grid-react";
import { isNil } from "lodash-es";
import { useEffect, useState } from "react";
import { Stand } from "../../models/Stand";
import { getStandsByPart } from "../../repositories/StandRepository";

type standsByPartsProps = {
	partId: number;
	onError: (error: Error) => void;
};

export const useStandsByPart = ({ partId, onError }: standsByPartsProps) => {
	return useQuery<Stand[], Error>({
		queryKey: ["standsByPart", partId],
		queryFn: ({ signal }) => getStandsByPart({ partId }, signal),
		onError: onError,
		enabled: !!partId,
	});
};

export const useStands = (onError: (error: Error) => void) => {
	return useQuery<Stand[], Error>({
		queryKey: ["stands"],
		queryFn: ({ signal }) => getStandsByPart({}, signal),
		onError: onError,
	});
};

type Props = {
	onSelectionChanged: Function;
	disabled?: boolean;
	standsOfPart: Stand[];
};

export const StandGrid = ({
	onSelectionChanged,
	disabled = false,
	standsOfPart,
}: Props) => {
	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { data: stands, isLoading } = useStands((error) =>
		setError(error.message),
	);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const { defaultColDef } = useGridDefinitions({});

	const columnDefs: ColDef[] = [
		{
			field: "StandName",
			headerName: "Applies to Stands",
			flex: 1,
		},
	];

	useEffect(() => {
		if (gridApi) {
			gridApi.forEachNode((node) => {
				node.setSelected(
					!!node.data &&
						!isNil(
							standsOfPart.find(
								(p) => p.StandId === (node.data as Stand).StandId,
							),
						),
				);
			});
		}
	}, [standsOfPart, gridApi]);

	return (
		<>
			<Grid2
				size={{ md: 12, xs: 12 }}
				style={{ height: "34vh" }}
				sx={{
					"& .ag-header-select-all": {
						marginLeft: 3.5,
					},
				}}
			>
				<AgGridReact<Stand>
					loading={isLoading}
					rowData={stands || []}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowHeight={22}
					headerHeight={30}
					animateRows={true}
					suppressContextMenu
					loadingOverlayComponent={CenteredLazyLoading}
					onSelectionChanged={onAgGridSelectionChanged}
					onGridReady={(params) => setGridApi(params.api)}
					getRowId={(params) => String(params.data.StandName)}
					gridOptions={{
						rowSelection: {
							mode: "multiRow",
							enableClickSelection: disabled,
							enableSelectionWithoutKeys: true,
							isRowSelectable: (_) => !disabled,
							hideDisabledCheckboxes: true,
							headerCheckbox: !disabled,
						},
					}}
				/>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
